'use client';

import { useEffect, useRef } from 'react';

interface TestimonialAmbienceProps {
  className?: string;
}

export default function TestimonialAmbience({ className = '' }: TestimonialAmbienceProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const orbsRef = useRef<any[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const setCanvasSize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // Create floating orbs
    const createOrbs = () => {
      const orbs = [];
      const colors = [
        { h: 40, s: 70, l: 60 },   // Gold
        { h: 260, s: 60, l: 55 },  // Purple
        { h: 180, s: 65, l: 50 },  // Cyan
        { h: 20, s: 80, l: 55 },   // Orange
        { h: 300, s: 70, l: 60 },  // Magenta
      ];

      for (let i = 0; i < 8; i++) {
        const color = colors[i % colors.length];
        orbs.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 60 + 40,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          color: color,
          pulsePhase: Math.random() * Math.PI * 2,
          pulseSpeed: Math.random() * 0.02 + 0.01,
        });
      }
      return orbs;
    };

    orbsRef.current = createOrbs();

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw orbs
      orbsRef.current.forEach((orb, index) => {
        // Update position
        orb.x += orb.vx;
        orb.y += orb.vy;

        // Bounce off edges
        if (orb.x < -50 || orb.x > canvas.width + 50) orb.vx *= -1;
        if (orb.y < -50 || orb.y > canvas.height + 50) orb.vy *= -1;

        // Keep in bounds
        orb.x = Math.max(-50, Math.min(canvas.width + 50, orb.x));
        orb.y = Math.max(-50, Math.min(canvas.height + 50, orb.y));

        // Update pulse
        orb.pulsePhase += orb.pulseSpeed;
        const pulseFactor = Math.sin(orb.pulsePhase) * 0.3 + 1;
        const currentRadius = orb.radius * pulseFactor;

        // Draw orb with gradient
        const gradient = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, currentRadius);
        const alpha = 0.4 + Math.sin(orb.pulsePhase) * 0.15;

        gradient.addColorStop(0, `hsla(${orb.color.h}, ${orb.color.s}%, ${orb.color.l}%, ${alpha})`);
        gradient.addColorStop(0.5, `hsla(${orb.color.h}, ${orb.color.s}%, ${orb.color.l}%, ${alpha * 0.6})`);
        gradient.addColorStop(1, `hsla(${orb.color.h}, ${orb.color.s}%, ${orb.color.l}%, 0)`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, currentRadius, 0, Math.PI * 2);
        ctx.fill();

        // Draw connections between nearby orbs
        orbsRef.current.forEach((otherOrb, otherIndex) => {
          if (otherIndex <= index) return;

          const dx = otherOrb.x - orb.x;
          const dy = otherOrb.y - orb.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 200) {
            const opacity = (1 - distance / 200) * 0.1;
            ctx.strokeStyle = `rgba(200, 169, 126, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(orb.x, orb.y);
            ctx.lineTo(otherOrb.x, otherOrb.y);
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas data-component-id="src/components/effects/TestimonialAmbience.tsx:128:4" data-component-path="src/components/effects/TestimonialAmbience.tsx" data-component-line="128" data-component-end-line="136" data-component-file="TestimonialAmbience.tsx" data-component-name="canvas" data-component-content="%7B%22elementName%22%3A%22canvas%22%7D"
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{
        filter: 'blur(30px)',
        width: '100%',
        height: '100%'
      }}
    />
  );
}
